import { ChipEstado } from '@/components/ui/ChipEstado'
import { IconoTipo } from '@/features/publicacion/IconoTipo'
import { MediaSlot } from '@/features/publicacion/MediaSlot'
import { cn } from '@/lib/cn'
import type { PublicacionResumen } from '@/lib/schemas/publicaciones'

/**
 * Una pieza dentro del calendario (§8.3): miniatura, chip abreviado, punto de
 * novedad e ícono de tipo. En la agenda del celular se suma el título, que en
 * la celda del mes no entra.
 *
 * La miniatura es la de Drive, pedida chica (200 px de ancho), con el
 * degradado guardado debajo mientras llega. Lo que el §8.3 evita es cargar el
 * archivo completo treinta veces, no la miniatura: es la misma que ya usa la
 * tarjeta de la grilla, y así el mes muestra el contenido de verdad.
 *
 * La hora, si tiene, va al lado del chip (DEC-20); una historia lleva el
 * anillo de color en la miniatura (DEC-21). Al pasar el mouse se levanta
 * como la tarjeta de la grilla, en chico (DEC-23).
 */
export interface PiezaCalendarioProps {
  publicacion: PublicacionResumen
  alAbrir: (id: string) => void
  /** La agenda del celular muestra el título; la celda del mes no. */
  conTitulo?: boolean
  /** El chip de "para revisar" dice otra cosa para RASTRO. */
  paraRastro?: boolean
  /**
   * Celda baja (monitor de 1360×768): miniatura y chip más chicos, para que
   * entren dos piezas por día sin desplazar el mes.
   */
  compacta?: boolean | undefined
  className?: string
  /**
   * RASTRO: el arrastre va en el mismo botón que abre la pieza. Un envoltorio
   * con su propio rol de botón dejaba un control dentro de otro, que el
   * lector de pantalla no sabe anunciar (axe, nested-interactive).
   */
  arrastre?: {
    ref: (el: HTMLElement | null) => void
    props: React.HTMLAttributes<HTMLElement>
  }
}

export function PiezaCalendario({
  publicacion: p,
  alAbrir,
  conTitulo,
  paraRastro,
  compacta,
  className,
  arrastre,
}: PiezaCalendarioProps) {
  return (
    <button
      {...arrastre?.props}
      ref={arrastre?.ref}
      type="button"
      data-pieza={p.titulo}
      data-destino={p.destino}
      aria-label={`Abrir ${p.titulo}${p.destino === 'historia' ? ' (historia)' : ''}`}
      onClick={() => {
        alAbrir(p.id)
      }}
      className={cn(
        'al-pasar al-pasar-chico flex w-full items-center gap-1.5 rounded-lg px-1 text-left',
        compacta ? 'py-0' : 'py-0.5',
        p.dadaDeBaja && 'opacity-60',
        className,
      )}
    >
      <MediaSlot
        driveFileId={p.portada?.driveFileId}
        blurhash={p.portada?.blurhash ?? undefined}
        miniaturaGuardada={p.portada?.miniatura}
        aspect="1:1"
        ancho={200}
        className={cn(
          'shrink-0 rounded-md',
          conTitulo ? 'w-11' : compacta ? 'w-[22px] rounded' : 'w-7',
          p.destino === 'historia' &&
            'ring-2 ring-historia ring-offset-1 ring-offset-tarjeta',
        )}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-1">
          <ChipEstado
            estado={p.estado}
            forma="corta"
            paraRastro={paraRastro}
            className={cn(
              'px-2 text-[11px] leading-4',
              compacta ? 'py-0 text-[10px]' : 'py-0.5',
            )}
          />
          {p.horaPautada && (
            <span
              data-hora
              className={cn(
                'shrink-0 text-tinta-suave tabular-nums',
                compacta ? 'text-[10px]' : 'text-[11px]',
              )}
            >
              {p.horaPautada}
            </span>
          )}
          {p.nuevo && (
            <span
              data-nuevo
              aria-label="Con novedades"
              className="size-1.5 shrink-0 rounded-full bg-nuevo"
            />
          )}
          {p.versionActual > 1 && (
            <span className="rounded-full bg-lienzo px-1.5 text-[11px] leading-4 text-tinta-suave">
              v{p.versionActual}
            </span>
          )}
        </span>
        {conTitulo && <span className="truncate text-[13px]">{p.titulo}</span>}
      </span>
      {p.tipo !== 'imagen' && (
        <IconoTipo
          tipo={p.tipo}
          className="flex shrink-0 text-tinta-suave"
          claseIcono="size-3.5"
        />
      )}
    </button>
  )
}
